import React, { useState, useEffect } from 'react';
import { Instagram, Linkedin, Facebook, Phone, Mail, MapPin } from 'lucide-react';
import { api } from '../services/api';

const DEFAULT_SETTINGS = {
  phone: '+54 11 4567-8900',
  email: 'info@sanzberger.com',
  address: 'Buenos Aires, Argentina',
  socialLinks: {
    instagram: 'https://instagram.com/sanzberger',
    linkedin: 'https://linkedin.com/company/sanzberger',
    facebook: 'https://facebook.com/sanzberger'
  }
};

const Footer = ({ revealed = true }) => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    api.getSettings().then((data) => {
      if (data) setSettings(data);
    });
  }, []);

  const socialLinks = [
    {
      name: 'Instagram',
      icon: <Instagram className="w-5 h-5" />,
      href: settings.socialLinks?.instagram || DEFAULT_SETTINGS.socialLinks.instagram,
      ariaLabel: "Visita nuestro Instagram"
    },
    {
      name: 'LinkedIn',
      icon: <Linkedin className="w-5 h-5" />,
      href: settings.socialLinks?.linkedin || DEFAULT_SETTINGS.socialLinks.linkedin,
      ariaLabel: "Visita nuestro LinkedIn"
    },
    {
      name: 'Facebook',
      icon: <Facebook className="w-5 h-5" />,
      href: settings.socialLinks?.facebook || DEFAULT_SETTINGS.socialLinks.facebook,
      ariaLabel: "Visita nuestro Facebook"
    }
  ];

  // Función para manejar clics en enlaces legales (puedes reemplazar con rutas reales)
  const handleLegalLink = (section) => {
    console.log(`Navegando a: ${section}`);
    // Aquí puedes usar React Router o window.location para navegar
    // Por ahora, solo muestra en consola
  };

  return (
    <div className="bg-secondary pt-6 pb-10 px-4 sm:px-6">
    <footer
      className={`max-w-7xl mx-auto bg-gray-900 text-white rounded-3xl shadow-2xl shadow-black/30 overflow-hidden py-12 px-4 sm:px-6 lg:px-8 transition-all duration-700 ease-out ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
        <div className="grid md:grid-cols-3 gap-10 items-start">
          {/* Columna 1: Logo + Texto */}
          <div className="text-center md:text-left">
            <div className="flex flex-col items-center md:items-start">
              {/* Logo en badge circular */}
              <div className="flex items-center gap-4 mb-4">
                <div className="h-16 w-16 shrink-0 rounded-full bg-white flex items-center justify-center overflow-hidden">
                  <img
                    src="/logo.png"
                    alt="Logo SANZ BERGER"
                    className="h-12 w-12 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                {/* Texto del nombre */}
                <div className="flex flex-col">
                  <h3 className="text-2xl font-bold">SANZ BERGER</h3>
                  <p className="text-gray-500 text-sm tracking-widest uppercase mt-1">
                    Bienes Raíces
                  </p>
                  <span className="inline-flex items-center w-fit mt-2 px-2.5 py-1 bg-gray-800 border border-gray-700 rounded-full text-xs font-medium text-gray-300">
                    CUCIS 88
                  </span>
                </div>
              </div>
              
              {/* Descripción breve */}
              <p className="text-gray-400 text-sm max-w-xs mb-4">
                Más de 20 años ayudando a nuestros clientes a encontrar su propiedad ideal y maximizar sus inversiones inmobiliarias.
              </p>
              
              {/* Información de contacto en columna */}
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{settings.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>{settings.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{settings.address}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Columna 2: Enlaces rápidos */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold mb-4 text-white">Enlaces Rápidos</h4>
            <ul className="space-y-1 text-gray-400 flex flex-col items-center md:items-start">
              <li>
                <a href="#propiedades" className="inline-block px-3 py-1.5 rounded-full hover:bg-gray-800 hover:text-white transition-colors">
                  Propiedades Disponibles
                </a>
              </li>
              <li>
                <a href="#propiedades" className="inline-block px-3 py-1.5 rounded-full hover:bg-gray-800 hover:text-white transition-colors">
                  Terrenos y Lotes
                </a>
              </li>
              <li>
                <a href="#propiedades" className="inline-block px-3 py-1.5 rounded-full hover:bg-gray-800 hover:text-white transition-colors">
                  Alquileres Temporales
                </a>
              </li>
              <li>
                <a href="#nosotros" className="inline-block px-3 py-1.5 rounded-full hover:bg-gray-800 hover:text-white transition-colors">
                  Oportunidades de Inversión
                </a>
              </li>
              <li>
                <a href="#contacto" className="inline-block px-3 py-1.5 rounded-full hover:bg-gray-800 hover:text-white transition-colors">
                  Servicio de Tasación
                </a>
              </li>
            </ul>
          </div>
          
          {/* Columna 3: Redes sociales */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold mb-4 text-white">Seguinos</h4>
            <div className="flex justify-center md:justify-start gap-4 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.ariaLabel}
                  className="text-gray-400 hover:text-white transition-colors flex flex-col items-center gap-1"
                >
                  <div className="p-3 bg-gray-800 rounded-full hover:bg-gray-700 transition-colors hover:scale-110">
                    {social.icon}
                  </div>
                  <span className="text-xs mt-1">{social.name}</span>
                </a>
              ))}
            </div>
            
            {/* Newsletter */}
            <div className="mt-6">
              <p className="text-sm text-gray-400 mb-2">Suscribite a nuestro newsletter</p>
              <div className="flex rounded-full bg-gray-800 focus-within:ring-2 focus-within:ring-primary/50 overflow-hidden">
                <input
                  type="email"
                  placeholder="Tu email"
                  className="px-4 py-2 bg-transparent text-white flex-1 min-w-0 focus:outline-none placeholder:text-gray-500"
                />
                <button className="m-1 px-4 py-1.5 bg-white text-gray-900 font-medium rounded-full hover:bg-gray-200 transition-colors">
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Línea divisoria */}
        <div className="mt-10 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="text-center md:text-left">
              <p className="text-gray-600 text-sm">
                © {currentYear} Sanz Berger Bienes Raíces. Todos los derechos reservados.
              </p>
              <p className="text-gray-600 text-xs mt-1">
                CUCIS 88
              </p>
            </div>
            
            {/* Enlaces legales - CORREGIDOS */}
            <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
              <button 
                onClick={() => handleLegalLink('terminos')}
                className="hover:text-gray-300 transition-colors bg-transparent border-none p-0 cursor-pointer"
              >
                Términos y Condiciones
              </button>
              <span className="text-gray-500">•</span>
              <button 
                onClick={() => handleLegalLink('privacidad')}
                className="hover:text-gray-300 transition-colors bg-transparent border-none p-0 cursor-pointer"
              >
                Política de Privacidad
              </button>
              <span className="text-gray-500">•</span>
              <button 
                onClick={() => handleLegalLink('legal')}
                className="hover:text-gray-300 transition-colors bg-transparent border-none p-0 cursor-pointer"
              >
                Aviso Legal
              </button>
            </div>
          </div>
        </div>
        
        {/* Especialidades */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm mb-3">Especialistas en:</p>
          <div className="flex flex-wrap justify-center gap-2">
            <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 transition-colors">
              Terrenos
            </span>
            <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 transition-colors">
              Casas
            </span>
            <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 transition-colors">
              Departamentos
            </span>
            <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 transition-colors">
              Alquileres
            </span>
            <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 transition-colors">
              Oficinas
            </span>
            <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 transition-colors">
              Inversiones
            </span>
            <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 transition-colors">
              Tasaciones
            </span>
          </div>
        </div>
    </footer>
    </div>
  );
};

export default Footer;